import { useState, useRef, useCallback } from 'react';
import ApiService from '../services/ApiService';

/**
 * Reusable hook for flag/report functionality
 * Works with Reviews, Articles, Events, and any content with flag capability
 *
 * @param {Object} options
 * @param {string} options.contentType - 'review' | 'article' | 'event' | 'comment'
 * @param {Function} options.isLoggedIn - Function that returns true if user is logged in
 * @param {Function} options.onAuthRequired - Callback when auth is required but user not logged in
 * @param {Function} options.onError - Callback on error (receives error message)
 * @param {Function} options.onSuccess - Optional callback on successful flag
 */
const useFlag = ({
  contentType = 'review',
  isLoggedIn,
  onAuthRequired,
  onError,
  onSuccess,
}) => {
  // Track which content user has flagged
  const [flaggedContent, setFlaggedContent] = useState({});

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentContentId, setCurrentContentId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [alreadyFlagged, setAlreadyFlagged] = useState(false);

  // Prevent double submissions
  const processingRef = useRef(false);

  // Flag reasons
  const flagReasons = [
    { value: '', label: 'Select a reason...' },
    { value: 'inappropriate', label: 'Inappropriate content' },
    { value: 'spam', label: 'Spam or advertising' },
    { value: 'harassment', label: 'Harassment or bullying' },
    { value: 'hate_speech', label: 'Hate speech' },
    { value: 'misinformation', label: 'Misinformation' },
    { value: 'off_topic', label: 'Off-topic or irrelevant' },
    { value: 'other', label: 'Other' },
    { value: 'personal_mention', label: 'This review mentions me - request takedown' },
  ];

  /**
   * Open flag modal for a piece of content
   */
  const openFlagModal = useCallback(async (contentId) => {
    // Check authentication
    if (isLoggedIn && !isLoggedIn()) {
      onAuthRequired?.();
      return;
    }

    setCurrentContentId(contentId);
    setAlreadyFlagged(false);
    setIsModalOpen(true);

    // Check if user already flagged this content
    try {
      const response = await ApiService.get(`moderation/flag/check/${contentId}`);
      if (response.data?.hasFlagged) {
        setAlreadyFlagged(true);
      }
    } catch (error) {
      console.error('Error checking flag status:', error);
    }
  }, [isLoggedIn, onAuthRequired]);

  /**
   * Close the flag modal
   */
  const closeFlagModal = useCallback(() => {
    setIsModalOpen(false);
    setCurrentContentId(null);
    setAlreadyFlagged(false);
  }, []);

  /**
   * Submit a flag for content
   */
  const submitFlag = useCallback(async (reason, message) => {
    if (!currentContentId) return { success: false, reason: 'no_content' };

    // Validation
    if (!reason) {
      onError?.('Please select a reason for flagging this content');
      return { success: false, reason: 'no_reason' };
    }

    if (!message || message.trim().length < 10) {
      onError?.('Please provide additional details (at least 10 characters)');
      return { success: false, reason: 'no_message' };
    }

    // Prevent double submission
    if (processingRef.current) {
      return { success: false, reason: 'in_progress' };
    }

    processingRef.current = true;
    setIsSubmitting(true);

    try {
      const response = await ApiService.post('moderation/flag', {
        contentType,
        contentId: currentContentId,
        reason,
        message: message.trim(),
      });

      if (response.data?.success) {
        // Mark as flagged
        setFlaggedContent(prev => ({
          ...prev,
          [currentContentId]: true
        }));

        // Close modal
        closeFlagModal();

        // Notify caller
        const wasAutoHidden = response.data?.autoHidden;
        onSuccess?.(currentContentId, wasAutoHidden);

        return {
          success: true,
          autoHidden: wasAutoHidden,
          message: wasAutoHidden
            ? 'Thank you for your report. This content has been automatically hidden pending admin review due to multiple community flags.'
            : 'Thank you for your report. Our team will review this content shortly.'
        };
      } else {
        throw new Error(response.data?.error || 'Failed to submit flag');
      }
    } catch (error) {
      console.error('Error flagging content:', error);
      const serverMessage = error.response?.data?.error;
      onError?.(serverMessage || 'Failed to submit flag. Please try again.');
      return { success: false, reason: 'api_error', error };
    } finally {
      processingRef.current = false;
      setIsSubmitting(false);
    }
  }, [contentType, currentContentId, closeFlagModal, onError, onSuccess]);

  /**
   * Check if content has been flagged by user
   */
  const isFlagged = useCallback((contentId) => {
    return flaggedContent[contentId] || false;
  }, [flaggedContent]);

  /**
   * Initialize flagged state (e.g., when loading data from server)
   */
  const initializeFlaggedState = useCallback((contentId, hasFlagged) => {
    setFlaggedContent(prev => ({ ...prev, [contentId]: hasFlagged }));
  }, []);

  return {
    // State
    isModalOpen,
    currentContentId,
    isSubmitting,
    alreadyFlagged,
    flagReasons,

    // Actions
    openFlagModal,
    closeFlagModal,
    submitFlag,
    initializeFlaggedState,

    // Getters
    isFlagged,
  };
};

export default useFlag;
